import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { addActiveUpload, removeActiveUpload } from "src/utils/activeUploads";
import { markUploadFailedOnUnload } from "src/utils/markUploadFailedOnUnload";
import {
  getStoredUploads,
  setStoredUpload,
  removeStoredUpload,
  getStoredUpload,
  STORAGE_KEY,
} from "src/utils/uploadStorage";

const DicomUploadContext = createContext();
export default DicomUploadContext;

export const useDicomUpload = () => useContext(DicomUploadContext);

export const DicomUploadProvider = ({ children }) => {
  const [uploads, setUploads] = useState(() => getStoredUploads());
  const beforeUnloadRefs = useRef({});

  const updateUpload = useCallback((jobRequestId, patch) => {
    const key = String(jobRequestId);
    setUploads((prev) => {
      const next = { ...prev, [key]: { ...prev[key], ...patch } };
      const merged = next[key];
      if (merged.status === "uploading" || merged.status === "finalizing") {
        setStoredUpload(jobRequestId, merged);
      } else if (merged.status === "completed" || merged.status === "error") {
        removeStoredUpload(jobRequestId);
      }
      return next;
    });
  }, []);

  const startUpload = useCallback(
    (jobRequestId, presignedUrl, file, finalizeUploadMutation, onUploadFailed) => {
      addActiveUpload(jobRequestId);
      // Initialise state (and sync to localStorage for other tabs)
      updateUpload(jobRequestId, {
        percent: 0,
        timeRemaining: "calculating...",
        status: "uploading",
        error: null,
      });

      const startTime = Date.now();

      // Prevent accidental page close and mark upload failed on reload/close
      const handleBeforeUnload = (e) => {
        const idsToMarkFailed = Object.keys(beforeUnloadRefs.current);
        if (idsToMarkFailed.length > 0) {
          markUploadFailedOnUnload(idsToMarkFailed);
        }
        e.preventDefault();
        const message =
          "The DICOM upload will fail if you reload or close this page";
        e.returnValue = message;
        return message;
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      beforeUnloadRefs.current[jobRequestId] = handleBeforeUnload;

      (async () => {
        try {
          const uploadResponse = await axios.put(presignedUrl, file, {
            headers: { "Content-Type": file.type || "application/zip" },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              // Min 1s for stable speed estimate at start (avoids remaining time jumping up)
              const elapsedTime = Math.max((Date.now() - startTime) / 1000, 1);
              const uploadSpeed = progressEvent.loaded / elapsedTime;
              const remainingBytes = progressEvent.total - progressEvent.loaded;
              const remainingTimeSeconds =
                uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0;

              const minutes = Math.floor(remainingTimeSeconds / 60);
              const seconds = Math.floor(remainingTimeSeconds % 60);
              const timeRemaining =
                minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

              updateUpload(jobRequestId, {
                percent: percentCompleted,
                timeRemaining,
                status: "uploading",
              });
            },
          });

          if (uploadResponse.status >= 200 && uploadResponse.status < 300) {
            updateUpload(jobRequestId, { percent: 100, status: "finalizing" });

            finalizeUploadMutation({
              variables: {
                input: {
                  jobRequestId,
                  filename: file.name,
                },
              },
              onCompleted: (response) => {
                if (response.finalizeUpload.success) {
                  updateUpload(jobRequestId, { status: "completed" });
                  removeActiveUpload(jobRequestId);
                } else {
                  updateUpload(jobRequestId, {
                    status: "error",
                    error: "Failed to finalize job request",
                  });
                  if (typeof onUploadFailed === "function") {
                    onUploadFailed(jobRequestId);
                  }
                  removeActiveUpload(jobRequestId);
                }
              },
              onError: () => {
                updateUpload(jobRequestId, {
                  status: "error",
                  error: "Error finalizing job request",
                });
                if (typeof onUploadFailed === "function") {
                  onUploadFailed(jobRequestId);
                }
                removeActiveUpload(jobRequestId);
              },
            });
          } else {
            updateUpload(jobRequestId, {
              status: "error",
              error: "Upload rejected by server",
            });
            if (typeof onUploadFailed === "function") {
              onUploadFailed(jobRequestId);
            }
            removeActiveUpload(jobRequestId);
          }
        } catch (err) {
          updateUpload(jobRequestId, {
            status: "error",
            error: `Upload failed: ${err.message}`,
          });
          if (typeof onUploadFailed === "function") {
            onUploadFailed(jobRequestId);
          }
          removeActiveUpload(jobRequestId);
        } finally {
          window.removeEventListener("beforeunload", handleBeforeUnload);
          delete beforeUnloadRefs.current[jobRequestId];
        }
      })();
    },
    [updateUpload],
  );

  const getUpload = useCallback(
    (jobRequestId) => uploads[String(jobRequestId)] ?? getStoredUpload(jobRequestId) ?? null,
    [uploads],
  );

  // Sync state from localStorage when another tab updates upload progress
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const next = JSON.parse(e.newValue);
          if (typeof next === "object" && next !== null) {
            setUploads(next);
          }
        } catch (_) {
          // Ignore
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const clearUpload = useCallback((jobRequestId) => {
    removeStoredUpload(jobRequestId);
    setUploads((prev) => {
      const next = { ...prev };
      delete next[String(jobRequestId)];
      return next;
    });
  }, []);

  const contextData = {
    uploads,
    startUpload,
    getUpload,
    clearUpload,
  };

  return (
    <DicomUploadContext.Provider value={contextData}>
      {children}
    </DicomUploadContext.Provider>
  );
};

DicomUploadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
